import api from './api';

// Types matching backend DTOs
export interface UserRef {
  id: string;
  name: string;
  role?: string;
  email?: string;
}

export interface Note {
  id: string;
  entityType: string;
  entityId: string;
  content: string;
  type?: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  author: UserRef;
  authorId: string;
  parentNoteId?: string;
  tags?: string[];
  attachments?: any[];
  isInternal?: boolean;
  isPinned?: boolean;
  visibleToRoles?: string[];
  isAlert?: boolean;
  alertUntil?: string | null;
  showOnOrder?: boolean;
  showOnDispensing?: boolean;
  showOnDelivery?: boolean;
  showOnBilling?: boolean;
  followUpRequired?: boolean;
  followUpDate?: string | null;
  followUpCompleted?: boolean;
  followUpCompletedBy?: UserRef | null;
  followUpCompletedAt?: string | null;
  metadata?: Record<string, any>;
  relatedOrderId?: string;
  relatedPrescriptionId?: string;
  relatedBillingItemId?: string;
  workflowStage?: string;
  workflowAction?: string;
  createdAt: string;
  updatedAt?: string | null;
  editedAt?: string | null;
  editedBy?: UserRef | null;
  deletedAt?: string | null;
  deletedBy?: UserRef | null;
  deletionReason?: string;
}

export interface CreateNoteDto {
  entityType: string;
  entityId: string;
  content: string;
  type?: string;
  priority?: string;
  author: UserRef;
  parentNoteId?: string;
  tags?: string[];
  attachments?: any[];
  isInternal?: boolean;
  isPinned?: boolean;
  visibleToRoles?: string[];
  isAlert?: boolean;
  alertUntil?: string;
  showOnOrder?: boolean;
  showOnDispensing?: boolean;
  showOnDelivery?: boolean;
  showOnBilling?: boolean;
  followUpRequired?: boolean;
  followUpDate?: string;
  relatedOrderId?: string;
  relatedPrescriptionId?: string;
  relatedBillingItemId?: string;
  workflowStage?: string;
  workflowAction?: string;
  metadata?: Record<string, any>;
}

export interface UpdateNoteDto {
  content?: string;
  type?: string;
  priority?: string;
  tags?: string[];
  attachments?: any[];
  isInternal?: boolean;
  isPinned?: boolean;
  visibleToRoles?: string[];
  isAlert?: boolean;
  alertUntil?: string;
  showOnOrder?: boolean;
  showOnDispensing?: boolean;
  showOnDelivery?: boolean;
  showOnBilling?: boolean;
  followUpRequired?: boolean;
  followUpDate?: string;
  metadata?: Record<string, any>;
  editedBy?: UserRef;
}

export interface DeleteNoteDto {
  deletedBy: UserRef;
  deletionReason?: string;
}

export interface QueryNotesDto {
  includeDeleted?: boolean;
  type?: string;
  types?: string[];
  priority?: string;
  authorId?: string;
  tags?: string[];
  pinnedOnly?: boolean;
  alertsOnly?: boolean;
  followUpRequired?: boolean;
  internalOnly?: boolean;
  parentNoteId?: string;
  workflowStage?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface NotesResponse {
  notes: Note[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface NotePermissions {
  canRead: boolean;
  canWrite: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canManageAll: boolean;
}

class NotesService {
  // Use the shared API instance with Auth0 authentication
  private api = api;
  private notesPath = '/notes';

  constructor() {
    // No need for additional interceptors - the shared api instance handles auth
  }

  /**
   * Create a new note
   */
  async createNote(
    entityType: string,
    entityId: string,
    data: Partial<CreateNoteDto>,
  ): Promise<Note> {
    const response = await this.api.post<Note>(
      `api${this.notesPath}/${entityType}/${entityId}`,
      data,
    );
    return response.data;
  }

  /**
   * Get all notes for an entity
   */
  async getNotes(
    entityType: string,
    entityId: string,
    query?: QueryNotesDto,
  ): Promise<NotesResponse> {
    const response = await this.api.get<NotesResponse>(
      `api${this.notesPath}/${entityType}/${entityId}`,
      {
        params: query,
      },
    );
    return response.data;
  }

  /**
   * Get a single note by ID
   */
  async getNote(id: string, includeDeleted = false): Promise<Note> {
    const response = await this.api.get<Note>(`api${this.notesPath}/note/${id}`, {
      params: { includeDeleted },
    });
    return response.data;
  }

  /**
   * Update a note
   */
  async updateNote(id: string, data: UpdateNoteDto): Promise<Note> {
    const response = await this.api.patch<Note>(`api${this.notesPath}/note/${id}`, data);
    return response.data;
  }

  /**
   * Soft delete a note
   */
  async deleteNote(id: string, data: DeleteNoteDto): Promise<Note> {
    const response = await this.api.delete<Note>(`api${this.notesPath}/note/${id}`, { data });
    return response.data;
  }

  /**
   * Restore a soft-deleted note
   */
  async restoreNote(id: string): Promise<Note> {
    const response = await this.api.post<Note>(`api${this.notesPath}/note/${id}/restore`);
    return response.data;
  }

  /**
   * Complete a follow-up
   */
  async completeFollowUp(id: string): Promise<Note> {
    const response = await this.api.post<Note>(`api${this.notesPath}/note/${id}/complete-followup`);
    return response.data;
  }

  /**
   * Toggle the pin status of a note
   */
  async togglePin(id: string): Promise<Note> {
    const response = await this.api.post<Note>(`api${this.notesPath}/note/${id}/toggle-pin`);
    return response.data;
  }

  /**
   * Get recent notes across all entities
   */
  async getRecentNotes(limit = 10, entityTypes?: string[]): Promise<Note[]> {
    const response = await this.api.get<Note[]>(`api${this.notesPath}/recent`, {
      params: { limit, entityTypes },
    });
    return response.data;
  }

  /**
   * Get notes requiring follow-up
   */
  async getFollowUpNotes(overdue = false): Promise<Note[]> {
    const response = await this.api.get<Note[]>(`api${this.notesPath}/followup/pending`, {
      params: { overdue },
    });
    return response.data;
  }

  /**
   * Get active alert notes
   */
  async getActiveAlerts(entityType?: string, entityId?: string): Promise<Note[]> {
    const response = await this.api.get<Note[]>(`api${this.notesPath}/alerts/active`, {
      params: { entityType, entityId },
    });
    return response.data;
  }

  /**
   * Get note permissions for current user
   */
  async getPermissions(entityType: string): Promise<NotePermissions> {
    const response = await this.api.get<NotePermissions>(
      `api${this.notesPath}/permissions/${entityType}`,
    );
    return response.data;
  }

  /**
   * Helper function for NotesPanel component (V2 API for patients)
   * Uses the new v2 endpoint for better REST conventions
   */
  async fetchNotesForPanel(opts: {
    entityType: string;
    entityId: string;
    includeDeleted: boolean;
  }): Promise<Note[]> {
    // Use V2 API for patients
    if (opts.entityType === 'patient') {
      const response = await this.api.get<NotesResponse>(
        `/api/v2/patients/${opts.entityId}/notes`,
        {
          params: {
            includeDeleted: opts.includeDeleted,
            limit: 100,
          },
        },
      );
      return response.data.notes;
    }

    // Use V1 API for other entity types
    const response = await this.getNotes(opts.entityType, opts.entityId, {
      includeDeleted: opts.includeDeleted,
      limit: 100, // Get more notes for panel
    });
    return response.notes;
  }

  /**
   * Helper function for NotesPanel component (V2 API for patients)
   */
  async createNoteForPanel(opts: {
    entityType: string;
    entityId: string;
    content: string;
    author: UserRef;
    type?: string;
    priority?: string;
    tags?: string[];
    showOnDispensing?: boolean;
    showOnOrder?: boolean;
    showOnDelivery?: boolean;
    showOnBilling?: boolean;
    isAlert?: boolean;
    metadata?: Record<string, any>;
    relatedOrderId?: string;
    relatedPrescriptionId?: string;
    workflowStage?: string;
  }): Promise<Note> {
    // Use V2 API for patients
    if (opts.entityType === 'patient') {
      const response = await this.api.post<Note>(
        `/api/v2/patients/${opts.entityId}/notes`,
        opts,
      );
      return response.data;
    }

    // Use V1 API for other entity types
    return this.createNote(opts.entityType, opts.entityId, opts);
  }

  /**
   * Helper function for NotesPanel component
   */
  async updateNoteForPanel(
    id: string,
    opts: { content: string; editedBy: UserRef; tags?: string[] },
  ): Promise<Note> {
    return this.updateNote(id, opts);
  }

  /**
   * Helper function for NotesPanel component
   */
  async deleteNoteForPanel(
    id: string,
    opts: { deletedBy: UserRef; deletionReason?: string },
  ): Promise<void> {
    await this.deleteNote(id, opts);
  }

  /**
   * Helper function for NotesPanel component
   */
  async restoreNoteForPanel(id: string): Promise<Note> {
    return this.restoreNote(id);
  }

  /**
   * Get all notes linked to a specific order
   */
  async getNotesForOrder(orderId: string): Promise<Note[]> {
    const response = await this.api.get<Note[]>(`/api/notes/by-order/${orderId}`);
    return response.data;
  }
}

// Export singleton instance
const notesService = new NotesService();
export default notesService;
